import React, { useMemo, useState } from 'react';
import { useI18n } from '../../i18n';
import ProductInfoDialog from './ProductInfoDialog';
import { formatProductPriceEur, priceAmountToEur } from './currency';

export const aiLookLanes = [
  {
    key: 'exact',
    icon: 'ph-seal-check',
    titleKey: 'aiDialog.laneExactTitle',
    copyKey: 'aiDialog.laneExactCopy',
  },
  {
    key: 'similar',
    icon: 'ph-sparkle',
    titleKey: 'aiDialog.laneSimilarTitle',
    copyKey: 'aiDialog.laneSimilarCopy',
  },
  {
    key: 'budget',
    icon: 'ph-tag',
    titleKey: 'aiDialog.laneBudgetTitle',
    copyKey: 'aiDialog.laneBudgetCopy',
  },
  {
    key: 'luxury',
    icon: 'ph-diamond',
    titleKey: 'aiDialog.laneLuxuryTitle',
    copyKey: 'aiDialog.laneLuxuryCopy',
  },
];

export function productImageUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (value.startsWith('/')) return value;
  return `/api/media/product-image?url=${encodeURIComponent(value)}`;
}

function productHasImage(candidate) {
  return Boolean(String(candidate?.image_url || '').trim());
}

function hideBrokenProductImage(event) {
  event.currentTarget.closest('button')?.remove();
}

export default function AiLookResults({ status, result, error, context = 'dialog', onRetry = null, onTryOn = null }) {
  const { locale, t } = useI18n();
  const [selectedProduct, setSelectedProduct] = useState(null);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  const matchedItems = (result?.items || []).map((item) => ({
    ...item,
    candidates: aiLookLanes.reduce((acc, lane) => {
      acc[lane.key] = (item.candidates?.[lane.key] || []).filter(productHasImage);
      return acc;
    }, {}),
  }));
  const visibleMatchedItems = matchedItems.filter((item) =>
    aiLookLanes.some((lane) => (item.candidates?.[lane.key] || []).length > 0),
  );
  const isPageContext = context === 'page';
  const totalCandidates = visibleMatchedItems.reduce(
    (sum, item) => sum + aiLookLanes.reduce((laneSum, lane) => laneSum + (item.candidates?.[lane.key] || []).length, 0),
    0,
  );
  const showReadyRetry = status === 'ready' && typeof onRetry === 'function';

  function formatPrice(candidate) {
    return formatProductPriceEur(candidate, currencyFormatter, t('detail.priceUnavailable'));
  }

  function laneMinPrice(item, laneKey) {
    const prices = (item.candidates?.[laneKey] || [])
      .map((candidate) => priceAmountToEur(candidate.price_amount, candidate.currency))
      .filter((price) => price !== null);
    if (prices.length === 0) return null;
    return currencyFormatter.format(Math.min(...prices));
  }

  function bestCandidate(item) {
    return aiLookLanes.flatMap((lane) => item.candidates?.[lane.key] || [])[0] || null;
  }

  if (status === 'loading' && matchedItems.length === 0) {
    return (
      <div className="ct-ai-state">
        <span className="ct-input-spinner"></span>
        <strong>{t('aiDialog.loadingTitle')}</strong>
        <p>{t(isPageContext ? 'aiDialog.loadingPageCopy' : 'aiDialog.loadingCopy')}</p>
      </div>
    );
  }

  if (status === 'error') {
    const translated = t(`aiDialog.error.${error}`);
    return (
      <div className="ct-ai-state">
        <i className="ph ph-warning-circle"></i>
        <strong>{t('aiDialog.errorTitle')}</strong>
        <p>{translated === `aiDialog.error.${error}` ? t('aiDialog.errorCopy') : translated}</p>
        {typeof onRetry === 'function' ? (
          <button type="button" className="ct-ai-state-action" onClick={onRetry}>
            <i className="ph ph-arrow-clockwise"></i>
            {t('aiDialog.retry')}
          </button>
        ) : null}
      </div>
    );
  }

  if (status === 'ready' && visibleMatchedItems.length === 0) {
    return (
      <div className="ct-ai-state">
        <i className="ph ph-magnifying-glass"></i>
        <strong>{t('aiDialog.emptyTitle')}</strong>
        <p>{t('aiDialog.emptyCopy')}</p>
      </div>
    );
  }

  if (status !== 'ready' && status !== 'loading') return null;

  return (
    <div className="ct-ai-results">
      {showReadyRetry ? (
        <div className="ct-ai-result-toolbar">
          <span>
            {totalCandidates > 0
              ? t('aiDialog.resultCount', { count: totalCandidates })
              : t('aiDialog.emptyTitle')}
          </span>
          <button type="button" className="ct-ai-state-action compact" onClick={onRetry}>
            <i className="ph ph-arrow-clockwise"></i>
            {t('aiDialog.retry')}
          </button>
        </div>
      ) : null}
      {status === 'loading' ? (
        <div className="ct-ai-live-note">
          <span className="ct-input-spinner"></span>
          <strong>{t('aiDialog.liveTitle')}</strong>
          <span>{t(isPageContext ? 'aiDialog.livePageCopy' : 'aiDialog.liveCopy')}</span>
        </div>
      ) : null}
      {visibleMatchedItems.map((item) => (
        <section className="ct-ai-item" key={item.id}>
          <div className="ct-ai-item-head">
            <div className="ct-ai-item-tags">
              <span>{item.category}</span>
              <span>{Number(item.confidence) < 0.65 ? t('aiDialog.vibeMatch') : t('aiDialog.lookMatch')}</span>
            </div>
            <strong>{item.description}</strong>
            <div className="ct-ai-quick-row">
              {bestCandidate(item) ? (
                <span className="ct-ai-best-pick">
                  <i className="ph ph-seal-check"></i>
                  {t('aiDialog.bestPick', { price: formatPrice(bestCandidate(item)) })}
                </span>
              ) : null}
              {['budget', 'similar', 'luxury'].map((laneKey) => {
                const price = laneMinPrice(item, laneKey);
                return price ? <span key={laneKey}>{t(`aiDialog.quick.${laneKey}`, { price })}</span> : null;
              })}
            </div>
          </div>
          <div className="ct-ai-lanes">
            {aiLookLanes.map((lane) => {
              const candidates = (item.candidates?.[lane.key] || []).filter(productHasImage);
              if (lane.key === 'exact' && candidates.length === 0) return null;
              return (
                <article className={`ct-ai-lane ${lane.key}`} key={lane.key}>
                  <div className="ct-ai-lane-head">
                    <i className={`ph ${lane.icon}`}></i>
                    <strong>{t(lane.titleKey)}</strong>
                  </div>
                  {candidates.length === 0 ? (
                    <p>{t('aiDialog.noLaneResults')}</p>
                  ) : (
                    <div className="ct-ai-products">
                      {candidates.slice(0, 3).map((candidate) => (
                        <button
                          type="button"
                          className={`ct-ai-product ${bestCandidate(item)?.id === candidate.id ? 'best' : ''}`}
                          onClick={() => setSelectedProduct({ ...candidate, lane: lane.key, itemCategory: item.category })}
                          key={candidate.id}
                        >
                          <span className="ct-ai-product-image">
                            <img src={productImageUrl(candidate.image_url)} alt={candidate.name} onError={hideBrokenProductImage} />
                          </span>
                          <span className="ct-ai-product-copy">
                            {bestCandidate(item)?.id === candidate.id ? <span className="ct-ai-product-badge">{t('aiDialog.best')}</span> : null}
                            <strong>{candidate.name}</strong>
                            <em>
                              {candidate.store ? `${candidate.store} · ` : ''}
                              {formatPrice(candidate)}
                            </em>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
      <ProductInfoDialog
        product={selectedProduct}
        formatPrice={formatPrice}
        onClose={() => setSelectedProduct(null)}
        onTryOn={onTryOn}
        t={t}
      />
    </div>
  );
}
